import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Que un error no deje la pantalla en blanco (GKTIBO-117).
 *
 * El PO, 2026-09-23: _«cuando lo seleccioné, se cayó la página… queda en
 * blanco»_.
 *
 * ## Por qué esto importa más que el bug que lo destapó
 *
 * **El panel no tenía ni un `ErrorBoundary`.** React, ante un error sin
 * capturar, desmonta el árbol entero: la persona ve blanco, no puede volver, y
 * para saber qué pasó hay que pedirle que abra la consola del navegador.
 *
 * En blanco es el peor modo de fallo posible porque *no deja rastro del lado
 * de quien lo sufre*. Un mensaje en pantalla convierte «se rompió» en un
 * diagnóstico.
 *
 * ## Por qué se muestra el texto del error
 *
 * «Algo salió mal» obliga a pedirle a alguien que reproduzca con la consola
 * abierta. El mensaje exacto es lo que permite arreglarlo con un solo reporte
 * — el mismo criterio de la regla F15 aplicado al navegador: una pieza que
 * falla dice **por qué**.
 *
 * No es una fuga de información: esto es el panel de administración, lo ve la
 * persona dueña de los datos.
 */

interface Props {
  children: ReactNode;
}

interface Estado {
  error: Error | null;
  /** En qué componente se rompió. Es lo único que dice DÓNDE. */
  donde: string | null;
}

export class SiAlgoSeRompe extends Component<Props, Estado> {
  state: Estado = { error: null, donde: null };

  static getDerivedStateFromError(error: Error): Partial<Estado> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // El stack de componentes sólo llega por acá, y es lo único que dice EN
    // QUÉ pieza se rompió. Con el mensaje solo —«n is not a function»— hay que
    // ir descartando hipótesis de a una; con esto se lee la pieza.
    console.error('el panel se rompió', error, info.componentStack);
    this.setState({ donde: (info.componentStack ?? '').split('\n').slice(0, 6).join('\n') });
  }

  render() {
    const { error, donde } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="algo-se-rompio" role="alert">
        <h1>Se rompió esta pantalla</h1>
        <p>
          No es culpa tuya y no perdiste nada de lo que ya estaba guardado. Si vuelve a pasar,
          pasanos este texto:
        </p>
        <pre className="algo-se-rompio-detalle">
          {error.message}
          {donde ? `\n${donde}` : ''}
        </pre>
        <button type="button" onClick={() => window.location.reload()}>
          Recargar
        </button>
      </div>
    );
  }
}
